// File: frontend/src/components/admin/common/SearchableSelect.tsx

import React, { useState, useEffect, useRef, useMemo } from "react";
import { FaTimes, FaChevronDown } from "react-icons/fa";
import "./SearchableSelect.css";

export interface SearchableSelectOption {
  /** Stable option value — empty string is reserved for the "all" option. */
  value: string;
  /** Text shown in the input once selected and inside the dropdown list. */
  label: string;
  /** Optional extra text shown greyed-out at the right side of a dropdown row. */
  hint?: string;
}

interface SearchableSelectProps {
  value: string;
  options: SearchableSelectOption[];
  onChange: (value: string) => void;
  /** Placeholder shown when nothing is selected — usually the "Tümü" label. */
  placeholder: string;
  disabled?: boolean;
}

/**
 * Turkish-safe normalization for substring matching.
 * Lowercases with the Turkish locale and strips diacritics so that typing
 * "tur" also matches "Türkiye" and "Besiktas" matches "Beşiktaş".
 */
const normalize = (input: string): string =>
  input
    .replace(/İ/g, "i")
    .replace(/I/g, "ı")
    .toLocaleLowerCase("tr")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ı/g, "i");

/**
 * Combobox used by the admin filter panels: behaves like a dropdown but the
 * input is also typeable, filtering the option list as the user types.
 */
export const SearchableSelect: React.FC<SearchableSelectProps> = ({
  value,
  options,
  onChange,
  placeholder,
  disabled = false,
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const selected = useMemo(
    () => options.find(o => o.value === value) ?? null,
    [options, value]
  );

  // Close the dropdown when clicking outside and drop any half-typed query
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    if (!query.trim()) return options;
    const q = normalize(query.trim());
    return options.filter(
      o => normalize(o.label).includes(q) || (o.hint ? normalize(o.hint).includes(q) : false)
    );
  }, [options, query]);

  // Keep the highlighted row inside the bounds of the current result list
  useEffect(() => { setHighlight(0); }, [query, open]);

  const commit = (option: SearchableSelectOption) => {
    onChange(option.value);
    setQuery("");
    setOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (open && filtered[highlight]) commit(filtered[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
    }
  };

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange("");
    setQuery("");
    setOpen(false);
  };

  // While the dropdown is open the input shows what the user types;
  // otherwise it shows the selected option label.
  const inputValue = open ? query : (selected ? selected.label : "");

  return (
    <div className="searchable-select" ref={wrapperRef}>
      <div className="searchable-select-control">
        <input
          type="text"
          className="searchable-select-input"
          value={inputValue}
          placeholder={selected ? selected.label : placeholder}
          disabled={disabled}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
        />
        {selected ? (
          <button
            type="button"
            className="searchable-select-clear"
            onClick={clear}
            tabIndex={-1}
          >
            <FaTimes size={10} />
          </button>
        ) : (
          <FaChevronDown className="searchable-select-caret" size={10} />
        )}
      </div>

      {open && (
        <ul className="searchable-select-dropdown">
          {filtered.length === 0 ? (
            <li className="searchable-select-empty">—</li>
          ) : (
            filtered.map((o, i) => (
              <li
                key={o.value || "__all__"}
                className={
                  (i === highlight ? "is-highlighted " : "") +
                  (o.value === value ? "is-selected" : "")
                }
                onMouseEnter={() => setHighlight(i)}
                onMouseDown={() => commit(o)}
              >
                <span>{o.label}</span>
                {o.hint && <span className="searchable-select-hint">{o.hint}</span>}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
};

export default SearchableSelect;